/**
 * Shared environmental interaction context — the single source of truth that the
 * shortcut engine, the gesture recognizer and the navigation (roving-focus)
 * system all consume.
 *
 * Before this module each system re-derived "is a dialog open / is an input
 * focused" independently: the engine kept a reactive `WhenContext`, while the
 * gesture hooks and the manual keyboard listener re-queried the DOM
 * (`document.querySelector('[role="dialog"]…')`) on every interaction. That
 * duplication caused drift and per-event DOM cost. Now there is one mutable
 * store; everyone reads it, and a small set of trackers keep it current.
 *
 * The store is framework-agnostic (Solid `createMutable`); the Solid bindings
 * live in `solid.tsx`. The flag keys are a superset of the shortcut engine's
 * `WhenContext`, so the same object can be handed to `<ShortcutsProvider>`.
 */

import { createMutable } from "solid-js/store";
import { isEditableElement } from "@/lib/dom/editable";

export interface InteractionState {
  /** An INPUT/TEXTAREA/SELECT or contenteditable currently holds focus. */
  "input.focused": boolean;
  /** Focus is inside editable text (everything above except SELECT). */
  "input.text-cursor": boolean;
  /** A modal dialog / alertdialog is open. */
  "dialog.open": boolean;
  /** The command palette is open. */
  "palette.open": boolean;
  /** The todo-detail drawer is open. */
  "drawer.open": boolean;
  /** A todo row is keyboard-focused in the list. */
  "todo.focused": boolean;
  /** Multi-select mode is active. */
  "selection.active": boolean;
  /** The user is currently navigating with the keyboard (drives focus rings). */
  "keyboard-active": boolean;
  [flag: string]: boolean;
}

function initialState(): InteractionState {
  return {
    "input.focused": false,
    "input.text-cursor": false,
    "dialog.open": false,
    "palette.open": false,
    "drawer.open": false,
    "todo.focused": false,
    "selection.active": false,
    "keyboard-active": false,
  };
}

export interface InteractionContext {
  /** The reactive store. Read flags inside JSX / effects to stay reactive. */
  readonly state: InteractionState;
  /** Set a flag directly (single-owner flags). */
  set: (flag: string, value: boolean) => void;
  /** Non-reactive read. */
  get: (flag: string) => boolean;
  /**
   * Refcounted contribution: many independent owners may raise the same flag
   * (e.g. two dialogs both raising `dialog.open`). The flag is true while at
   * least one contributor's value is true. Each owner identifies itself with a
   * stable `token` symbol.
   */
  contribute: (flag: string, token: symbol, value: boolean) => void;
}

export function createInteractionContext(): InteractionContext {
  const state = createMutable<InteractionState>(initialState());
  const contributors = new Map<string, Set<symbol>>();

  const set: InteractionContext["set"] = (flag, value) => {
    // `state` is a createMutable proxy; direct assignment IS the API.
    // eslint-disable-next-line solid/reactivity
    state[flag] = value;
  };

  const contribute: InteractionContext["contribute"] = (flag, token, value) => {
    let owners = contributors.get(flag);
    if (!owners) {
      owners = new Set();
      contributors.set(flag, owners);
    }
    if (value) {
      owners.add(token);
    } else {
      owners.delete(token);
    }
    // eslint-disable-next-line solid/reactivity
    state[flag] = owners.size > 0;
  };

  return {
    state,
    set,
    get: (flag) => state[flag] === true,
    contribute,
  };
}

/**
 * Track which kind of element holds focus into `input.focused` /
 * `input.text-cursor`. Returns a cleanup function. Capture-phase listeners so
 * the flags are up to date before any keydown is dispatched.
 */
export function trackFocus(ctx: InteractionContext, target: Document = document): () => void {
  const update = () => {
    const el = target.activeElement;
    const editable = isEditableElement(el);
    ctx.set("input.focused", editable);
    ctx.set("input.text-cursor", editable && el.tagName !== "SELECT");
  };
  target.addEventListener("focusin", update, { capture: true });
  target.addEventListener("focusout", update, { capture: true });
  update();
  return () => {
    target.removeEventListener("focusin", update, { capture: true });
    target.removeEventListener("focusout", update, { capture: true });
  };
}

const KEYBOARD_NAV_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "j",
  "k",
  "h",
  "l",
  " ",
  "Enter",
  "Escape",
  "?",
]);

/**
 * Track whether the user is driving the UI with the keyboard, into
 * `keyboard-active`, and mirror it onto `<body class="keyboard-navigation">`
 * so existing CSS focus-ring rules keep working. Replaces the standalone
 * listeners that previously lived in `features/layout/helpers.ts`.
 */
export function trackKeyboardActivity(
  ctx: InteractionContext,
  target: EventTarget = globalThis,
): () => void {
  const body = typeof document === "undefined" ? undefined : document.body;
  const activate = (event: Event) => {
    if (event instanceof KeyboardEvent && !KEYBOARD_NAV_KEYS.has(event.key)) {
      return;
    }
    ctx.set("keyboard-active", true);
    body?.classList.add("keyboard-navigation");
  };
  const deactivate = () => {
    ctx.set("keyboard-active", false);
    body?.classList.remove("keyboard-navigation");
  };
  target.addEventListener("keydown", activate, { capture: true });
  target.addEventListener("mousedown", deactivate, { capture: true });
  target.addEventListener("pointerdown", deactivate, { capture: true });
  return () => {
    target.removeEventListener("keydown", activate, { capture: true });
    target.removeEventListener("mousedown", deactivate, { capture: true });
    target.removeEventListener("pointerdown", deactivate, { capture: true });
  };
}
